
import ServiceTemplate from '../../ServiceTemplate';
import SEOHead from '../../../../components/SEOHead';

export default function EVChargerInstallationPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'EV Charger Installation',
    provider: {
      '@type': 'ElectricalContractor',
      name: 'Fresno Electrical Services',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Fresno',
        addressRegion: 'CA',
        addressCountry: 'US',
      },
    },
    areaServed: {
      '@type': 'City',
      name: 'Fresno',
      '@id': 'https://en.wikipedia.org/wiki/Fresno,_California',
    },
    description:
      'Professional Level 2 EV charger installation for all electric vehicles in Fresno, CA. Fast home charging with federal tax credits and California rebates.',
    url: `${siteUrl}/services/renewables-ev/ev-charger-installation`,
  };

  return (
    <>
      <SEOHead
        title="EV Charger Installation Fresno CA | Level 2 Home Charging"
        description="Professional Level 2 EV charger installation in Fresno, CA. Fast 240V home charging for all electric vehicles. Federal tax credits and California rebates available."
        canonical="/services/renewables-ev/ev-charger-installation"
        keywords="EV charger installation Fresno, Level 2 charger, home EV charging, electric vehicle charger"
        schema={schema}
      />
      <ServiceTemplate
        heroTitle="EV Charger Installation — Fresno, CA"
        heroSubtitle="Fast, reliable home charging for your electric vehicle"
        heroImage="https://public.readdy.ai/ai/img_res/0f0205c73c3431ba76cffea9941bd2e8.jpg"
        fresnoImage="https://readdy.ai/api/search-image?query=Close%20up%20detail%20of%20a%20sleek%20Level%202%20EV%20charging%20station%20control%20panel%20with%20digital%20display%20showing%20charging%20status%20and%20power%20level%20mounted%20on%20garage%20wall%20with%20electrical%20conduit%20visible%20professional%20installation%20California%20residential%20setting%20no%20people&width=700&height=600&seq=ev-charger-fresno-001&orientation=portrait"
        fresnoContent="Fresno's growing electric vehicle adoption makes home EV charger installation essential for convenient daily charging. With over 270 sunny days per year, Fresno is perfect for pairing EV charging with solar power. Our certified electricians install Level 2 charging stations that fully charge your EV overnight, eliminating range anxiety and saving you money compared to public charging stations. We serve all of Fresno County including Clovis, Sanger, and surrounding Central Valley communities, helping you take advantage of federal tax credits up to $1,000 and California's Clean Vehicle Rebate Program."
        features={[
          {
            icon: 'ri-charging-pile-2-line',
            title: 'Level 2 Charger Installation',
            description: 'Professional 240V charging stations for fast home charging',
          },
          {
            icon: 'ri-speed-line',
            title: 'Fast Charging Speed',
            description: 'Add 25-30 miles of range per hour, full charge in 4-6 hours',
          },
          {
            icon: 'ri-plug-2-line',
            title: 'Dedicated Circuit Installation',
            description: 'New 40-50 amp circuit from your electrical panel',
          },
          {
            icon: 'ri-smartphone-line',
            title: 'Smart Charging Features',
            description: 'WiFi-enabled chargers with mobile app control',
          },
          {
            icon: 'ri-money-dollar-circle-line',
            title: 'Rebate Assistance',
            description: 'Help with federal and California rebate applications',
          },
          {
            icon: 'ri-shield-check-line',
            title: 'Code Compliant Installation',
            description: 'All work meets NEC and California Title 24 standards',
          },
        ]}
        whatsIncluded={[
          'Complete electrical assessment and load calculation',
          'City of Fresno building permits and inspections',
          'Professional Level 2 EV charger installation (240V, 40-50 amp)',
          'Dedicated circuit installation from electrical panel to garage',
          'NEMA 14-50 outlet or hardwired charger connection',
          'Wall mounting with cable management system',
          'Smart charger setup and WiFi configuration',
          'Mobile app installation and training',
          'Federal tax credit application assistance (up to $1,000)',
          'California Clean Vehicle Rebate Program support',
          'Complete system testing and safety verification',
          '2-year workmanship warranty on installation',
        ]}
        faqs={[
          {
            question: 'How much does EV charger installation cost in Fresno?',
            answer:
              'EV charger installation in Fresno typically costs $800-$2,500 depending on your electrical panel capacity, distance from panel to garage, and charger model. This includes the dedicated 240V circuit, permits, and labor. If your panel needs upgrading (common in older Fresno homes), add $1,500-$3,000. However, the federal tax credit covers 30% of installation costs (up to $1,000), and California rebates can provide an additional $500-$2,000 for qualifying households. Most Fresno homeowners see a net cost of $500-$1,500 after incentives.',
          },
          {
            question: 'How long does EV charger installation take in Fresno homes?',
            answer:
              'Most residential EV charger installations in Fresno are completed in 4-6 hours for straightforward installations. This includes running the dedicated circuit, mounting the charger, and final testing. If we need to upgrade your electrical panel or run conduit through walls, it may take 1-2 days. City of Fresno permit processing typically adds 1-2 weeks to the timeline, but we handle all paperwork and inspections. We schedule installations at your convenience and ensure minimal disruption to your daily routine.',
          },
          {
            question: 'What rebates and incentives are available for EV chargers in Fresno?',
            answer:
              "Fresno residents can access multiple EV charger incentives. The federal Alternative Fuel Infrastructure Tax Credit covers 30% of installation costs up to $1,000 for residential installations. California's Clean Vehicle Rebate Project offers additional rebates, and PG&E provides incentives through their EV Charge Network program. Low-income Fresno households may qualify for enhanced rebates up to $2,000 through the Clean Cars 4 All program. We help you navigate all available programs and complete applications to maximize your savings.",
          },
          {
            question: 'Can my Fresno home electrical panel handle an EV charger?',
            answer:
              'Most modern Fresno homes with 200-amp electrical panels can accommodate a Level 2 EV charger. However, many homes built before 2000 have 100-amp panels that may need upgrading. A Level 2 charger requires a dedicated 40-50 amp circuit. We perform a complete load calculation during our free assessment to determine if your panel has sufficient capacity. If an upgrade is needed, we handle everything including permits, panel replacement, and City of Fresno inspections. Panel upgrades also increase your home value and prepare you for future electrical needs.',
          },
          {
            question: "What's the difference between Level 1 and Level 2 EV charging?",
            answer:
              "Level 1 charging uses your standard 120V household outlet and adds about 3-5 miles of range per hour. It's fine for plug-in hybrids or if you drive less than 40 miles daily in Fresno. Level 2 charging uses 240V (like your dryer) and adds 25-30 miles per hour, fully charging most EVs in 4-6 hours. For Fresno's commuting patterns and hot summers (which reduce EV range), we strongly recommend Level 2 chargers. They provide the convenience of waking up to a fully charged vehicle every morning.",
          },
          {
            question: 'Do I need permits for EV charger installation in Fresno?',
            answer:
              'Yes, EV charger installation requires an electrical permit from the City of Fresno Building Department. This ensures your installation meets National Electrical Code (NEC) and California Title 24 energy standards. We handle all permit applications, engineering drawings, and inspections as part of our service. Fresno typically processes residential EV charger permits within 2-3 weeks. Proper permitting protects you, ensures insurance coverage, and is required for claiming federal and state rebates.',
          },
        ]}
        ctaText="Get Your Free EV Charger Quote"
      />
    </>
  );
}
